import React from "react";
import PropTypes from "prop-types";
import styles from "./OrderHistoryItem.module.css";
import Button from "shared/Button";

export function OrderHistoryItem({
  orderNum,
  trackingNum,
  placedOn,
  expectedDelivery,
  numOfItems,
  item = [],
  total,
  status,
  onView,
  isWwgOrder,
}) {
  const items = Array.isArray(item) ? item : [];
  const previewItems = items.slice(0, 3);
  const remainingCount = Math.max(items.length - previewItems.length, 0);

  const [brokenImages, setBrokenImages] = React.useState(new Set());

  const statusClass =
    status?.toLowerCase() === "delivered"
      ? styles.delivered
      : status?.toLowerCase() === "cart"
      ? styles.cart
      : status?.toLowerCase() === "processing"
      ? styles.processing
      : status?.toLowerCase() === "cancelled"
      ? styles.cancelled
      : "";

  const formattedTotal =
    typeof total === "number"
      ? total.toLocaleString("en-US", { style: "currency", currency: "USD" })
      : total;

  const fallbackMetaValue = (value) => value || "--";

  return (
    <section className={styles.card}>
      <div className={styles.infoColumn}>
        <div className={styles.infoRow}>
          <span className={`${styles.infoLabel} ${styles.orderLabel}`}>
            Order #
          </span>
          <span className={`${styles.infoValue} ${styles.orderValue}`}>
            {orderNum || "--"}
          </span>
        </div>
        <div className={styles.infoRow}>
          <span className={`${styles.infoLabel} ${styles.trackingLabel}`}>
            Tracking #
          </span>
          <span className={`${styles.infoValue} ${styles.trackingValue}`}>
            {fallbackMetaValue(trackingNum)}
          </span>
        </div>
        <div className={styles.infoRow}>
          <span className={`${styles.infoLabel} ${styles.dateLabel}`}>
            Order Placed
          </span>
          <span className={`${styles.infoValue} ${styles.dateValue}`}>
            {fallbackMetaValue(placedOn)}
          </span>
        </div>
        <div className={styles.infoRow}>
          <span className={`${styles.infoLabel} ${styles.dateLabel}`}>
            Expected Delivery
          </span>
          <span className={`${styles.infoValue} ${styles.dateValue}`}>
            {fallbackMetaValue(expectedDelivery)}
          </span>
        </div>
        <div className={styles.infoRow}>
          <span className={`${styles.infoLabel} ${styles.itemsLabel}`}>
            Items Ordered
          </span>
          <span className={styles.infoValue}>
            {numOfItems != null ? `${numOfItems}` : "--"}
          </span>
        </div>
      </div>

      <div className={styles.itemsColumn} aria-label="Items in this order">
        {previewItems.length > 0 ? (
          <>
            {previewItems.map((previewItem, index) => {
              const itemKey = previewItem.id ?? index;
              const broken = brokenImages.has(itemKey);
              const initial =
                typeof previewItem.name === "string" &&
                previewItem.name.trim().length > 0
                  ? previewItem.name.trim().charAt(0).toUpperCase()
                  : "I";
              return (
                <div className={styles.itemThumb} key={itemKey}>
                  {previewItem.image && !broken ? (
                    <img
                      src={previewItem.image}
                      alt={previewItem.name ?? "Ordered item"}
                      onError={() =>
                        setBrokenImages((prev) => new Set(prev).add(itemKey))
                      }
                    />
                  ) : (
                    <div className={styles.itemInitial}>{initial}</div>
                  )}
                </div>
              );
            })}
            {remainingCount > 0 && (
              <span className={styles.remainingCount}>+{remainingCount}</span>
            )}
          </>
        ) : (
          <span className={styles.noItems}>No images</span>
        )}
      </div>

      <div className={styles.summarySection}>
        <div className={styles.totalColumn}>
          <span className={styles.totalValue}>{formattedTotal || "--"}</span>
        </div>

        <div className={styles.statusColumn}>
          {status && <span className={statusClass}>{status}</span>}
        </div>

        <div className={styles.actionColumn}>
          <Button
            type="button"
            className={styles.viewButton}
            title={`View the full details of order #${orderNum || ""}.`}
            onClick={onView}
          >
            View
          </Button>
        </div>
      </div>

      {isWwgOrder && (
        <div className={styles.wwgCallout}>WWG Order</div>
      )}
    </section>
  );
}

OrderHistoryItem.propTypes = {
  orderNum: PropTypes.string,
  trackingNum: PropTypes.string,
  placedOn: PropTypes.string,
  expectedDelivery: PropTypes.string,
  numOfItems: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  item: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.string,
    })
  ),
  total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  status: PropTypes.string,
  onView: PropTypes.func,
  isWwgOrder: PropTypes.bool,
};
